<template>
  <div>
    <section class="content-group -center -vs-large -vs-collapse-bottom -hs-small">
      <!-- error state -->
      <callout v-if="error" type="error">
        <div>{{ error }}</div>
      </callout>

      <div class="group -full -flex -between -vs-center -center">
        <h1 id="spa-title" class="heading -primary">Achievements</h1>
      </div>

      <!-- toggle switch -->
      <div
        v-if="resultsReady"
        class="group -full -flex -between -vs-center -center page-toggle-switch"
      >
        <router-link
          class="toggle-text"
          :class="active === 'complete' ? 'active' : ''"
          :to="'complete'"
        >
          Complete
        </router-link>
        <div
          role="button"
          tabindex="0"
          class="page-toggle"
          :aria-label="
            active === 'complete' ? 'View Available Achievements' : 'View Completed Achievements'
          "
          @click.prevent="pushRouteUpdate(active === 'complete' ? 'available' : 'complete')"
          @keyup.enter="pushRouteUpdate(active === 'complete' ? 'available' : 'complete')"
        >
          <span class="toggle-tab" :class="active === 'complete' ? 'active' : ''">
            <i class="material-symbols-outlined fill" aria-hidden="true">assignment_turned_in</i>
          </span>
          <span class="toggle-tab" :class="active === 'available' ? 'active' : ''">
            <i class="material-symbols-outlined fill" aria-hidden="true">assignment</i>
          </span>
        </div>
        <router-link
          class="toggle-text"
          :class="active === 'available' ? 'active' : ''"
          :to="'available'"
        >
          Available
        </router-link>
      </div>
    </section>

    <!-- loading -->
    <spinner-loader v-if="loading" />

    <!-- first time / no results messages -->
    <section v-if="showMessage" class="content-group -narrow -column -center -vs-small -hs-small">
      <div class="group -flex -wide -vs-center achievement">
        <div v-if="showFirstTimeMessage" class="achievement-info">
          <h1 class="title">We Are Processing Your Achievement Progress</h1>
          <div class="message">
            <p>
              It may take a moment to process your current progress. Check back soon to see if you
              have earned any achievements, or Check out all the currently
              <router-link :to="'available'"> available achievements </router-link>
              to see your progress.
            </p>
          </div>
        </div>
        <div v-else-if="showNoCompleteMessage" class="achievement-info">
          <h1 class="title">You Haven't Earned an Achievement Yet</h1>
          <div class="message">
            <p>
              Keep using the website, completing workouts, challenges, and programs, and
              participating in the community. Just about everything you do will make progress toward
              a current or future achievement. Check out all the currently
              <router-link :to="'available'"> available achievements </router-link>
              to see your progress.
            </p>
          </div>
        </div>
        <div v-else-if="showNoAvailableMessage" class="achievement-info">
          <h1 class="title">Check Back for More Achievements!</h1>
          <div class="message">
            <p>
              You have made progress on all current achievements. We'll be adding more soon, until
              then, continue using the website, completing workouts, challenges, and programs, and
              participating in the community. View
              <router-link :to="'complete'"> complete achievements </router-link> to see your
              progress.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- achievements -->
    <section
      v-if="resultsReady && active === 'complete' && !firstTime"
      class="content-group -narrow -column -center -vs-small -hs-small"
    >
      <achievement-card
        v-for="achievement in userAchievements"
        :id="`UA${achievement.id}`"
        :key="achievement.id"
        :achievement="achievement"
        :achievement-type="active"
      />
    </section>
    <section
      v-if="resultsReady && active === 'available'"
      class="content-group -narrow -column -center -vs-small -hs-small"
    >
      <achievement-card
        v-for="achievement in availableAchievements"
        :key="achievement.id"
        :achievement="achievement"
        :achievement-type="active"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
/**
 * Achievements page
 */
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Callout from '@/components/notifications/callout.vue'
import AchievementCard from '@/components/achievements/achievement-card.vue'
import SpinnerLoader from '@/components/loaders/spinner-loader.vue'
import { fbApi } from '@/utils/http-api'
import { scrollToElemTop } from '@/utils/scroll-to'
import type { Achievement } from '@/types/achievement-types'

/** PROPS **/
// none, SPA entry point

/** STATE **/
const route = useRoute()
const router = useRouter()

const userAchievements = ref<Achievement[] | null>(null)
const availableAchievements = ref<Achievement[] | null>(null)
const active = ref(route.params.type)
const firstTime = ref(false)
const error = ref('')
const loading = ref(false)

/** COMPUTED **/
const resultsReady = computed(() => {
  return (
    !error.value &&
    !loading.value &&
    ((Array.isArray(userAchievements.value) && userAchievements.value.length) ||
      (Array.isArray(availableAchievements.value) && availableAchievements.value.length))
  )
})

const showFirstTimeMessage = computed(() => resultsReady.value && firstTime.value)

const showNoCompleteMessage = computed(() => {
  return (
    resultsReady.value &&
    !firstTime.value &&
    Array.isArray(userAchievements.value) &&
    !userAchievements.value.length &&
    active.value === 'complete'
  )
})

const showNoAvailableMessage = computed(() => {
  return (
    resultsReady.value &&
    !firstTime.value &&
    Array.isArray(availableAchievements.value) &&
    !availableAchievements.value.length &&
    active.value === 'available'
  )
})

const showMessage = computed(
  () => showFirstTimeMessage.value || showNoCompleteMessage.value || showNoAvailableMessage.value,
)

/** WATCHERS **/
watch(
  () => route.params.type,
  (newType) => {
    active.value = newType
  },
)

/** LIFECYCLE **/
onMounted(() => {
  fetchAchievements()
})

/** METHODS **/
async function fetchAchievements() {
  if (loading.value) {
    return
  }

  loading.value = true
  error.value = ''

  try {
    const res = await fbApi.get('/achievements')

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch achievements, please refresh the page and try again`)
    }

    loading.value = false
    error.value = ''

    const data = res.body.data

    userAchievements.value = data.completeAchievements
    availableAchievements.value = data.availableAchievements
    firstTime.value = data.firstTime

    // scroll to achievement if hash provided from notification
    setTimeout(scrollToAchievement, 30)
  } catch (err) {
    loading.value = false
    error.value = `Unable to fetch achievements, please refresh the page and try again`
  }
}

function scrollToAchievement() {
  if (window && window.location && window.location.hash) {
    const scrollPoint = document.querySelector(window.location.hash)
    if (scrollPoint && scrollPoint instanceof HTMLElement) {
      scrollToElemTop({
        elem: scrollPoint,
        ms: 250,
      })
    }
  }
}

function pushRouteUpdate(type: string) {
  // force exact route
  type = type === 'available' ? 'available' : 'complete'
  router.push({ path: type }).catch(() => {})
  active.value = type
}
</script>
